/**
 * Dashboard Filters Component
 *
 * Handles all filter dropdowns for the dashboard.
 * Separated for better maintainability and reusability.
 */

import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../../store';
import { FilterDropdown } from '../FilterDropdown';
import { FILTER_OPTIONS } from '../constants/DashboardData';
import { getSelectedOptionName, formatComparisonLabel } from '../utils/DashboardUtils';
import { getComparisonOptionsForYear } from '../utils/DashboardDateUtils';
import { useSalesDashboardContext } from '../context/SalesDashboardProvider';
import type { DropdownState } from '../types/DashboardTypes';
import { fetchLocationsThunk } from '../../../actions/location/LocationActions';
import { LocationStatus } from '../../../reducers/locations/LocationState';
import styles from '../DashboardComponent.module.scss';
import { faCalendarAlt, faMapMarkerAlt, faStore } from '@fortawesome/pro-regular-svg-icons';

interface DashboardFiltersProps {
    expandedDropdowns: DropdownState;
    onDropdownToggle: (dropdownId: keyof DropdownState) => void;
    onDropdownClose: (dropdownId: keyof DropdownState) => void;
}

export const DashboardFilters: React.FC<DashboardFiltersProps> = ({
    expandedDropdowns,
    onDropdownToggle,
    onDropdownClose
}) => {
    const { UIFilters: filterValues, filterActions } = useSalesDashboardContext();
    const { locations, status: locationsStatus } = useSelector((state: RootState) => state.locations);
    const businessTypes = useSelector((state: RootState) => state.masterData.data.businessTypes);
    const dispatch = useDispatch();

    useEffect(() => {
        if (locationsStatus === LocationStatus.Initial) {
            dispatch(fetchLocationsThunk(1, 100, '') as any);
        }
    }, [dispatch, locationsStatus]);

    const dynamicComparisonOptions = useMemo(() => {
        return getComparisonOptionsForYear(filterValues.year);
    }, [filterValues.year]);

    const locationOptions = useMemo(() => {
        const defaultOps = [{ id: 'all', name: 'All locations' }];
        if (locations && locations.length > 0) {
            return [
                ...defaultOps,
                ...locations.map(loc => ({
                    id: loc.id?.toString() || '',
                    name: loc.businessName
                }))
            ];
        }
        return defaultOps;
    }, [locations]);

    const businessTypeOptions = useMemo(() => {
        const defaultOps = [{ id: 'all', name: 'All business types' }];
        if (businessTypes && businessTypes.length > 0) {
            return [
                ...defaultOps,
                ...businessTypes.map(type => ({
                    id: type.id,
                    name: type.name
                }))
            ];
        }
        return defaultOps;
    }, [businessTypes]);

    return (
        <div className={styles.filtersRow}>
            <FilterDropdown
                id="year"
                icon={faCalendarAlt}
                label={getSelectedOptionName(FILTER_OPTIONS.year, filterValues.year)}
                options={FILTER_OPTIONS.year}
                selectedOptionId={filterValues.year}
                expanded={expandedDropdowns.year}
                onToggle={() => onDropdownToggle('year')}
                onClose={() => onDropdownClose('year')}
                onSelect={filterActions.setYear}
                className={styles.filterDropdown}
                classNameList={styles.listYear}
                dropdownAlign="right"
            />

            <FilterDropdown
                id="comparison"
                label={formatComparisonLabel(getSelectedOptionName(dynamicComparisonOptions, filterValues.comparison))}
                options={dynamicComparisonOptions}
                selectedOptionId={filterValues.comparison}
                expanded={expandedDropdowns.comparison}
                onToggle={() => onDropdownToggle('comparison')}
                onClose={() => onDropdownClose('comparison')}
                onSelect={filterActions.setComparison}
                className={styles.filterDropdown}
                classNameList={styles.listCompareTo}
                dropdownAlign="right"
            />

            <FilterDropdown
                id="businessType"
                icon={faStore}
                label={getSelectedOptionName(businessTypeOptions, filterValues.businessType)}
                options={businessTypeOptions}
                selectedOptionId={filterValues.businessType}
                expanded={expandedDropdowns.businessType}
                onToggle={() => onDropdownToggle('businessType')}
                onClose={() => onDropdownClose('businessType')}
                onSelect={filterActions.setBusinessType}
                className={styles.filterDropdown}
                classNameList={styles.listBusinessType}
                dropdownAlign="right"
            />

            <FilterDropdown
                id="region"
                icon={faMapMarkerAlt}
                label={getSelectedOptionName(FILTER_OPTIONS.region, filterValues.region)}
                options={FILTER_OPTIONS.region}
                selectedOptionId={filterValues.region}
                expanded={expandedDropdowns.region}
                onToggle={() => onDropdownToggle('region')}
                onClose={() => onDropdownClose('region')}
                onSelect={filterActions.setRegion}
                className={styles.filterDropdown}
                classNameList={styles.listRegion}
                dropdownAlign="right"
            />

            <FilterDropdown
                id="location"
                icon={faMapMarkerAlt}
                label={getSelectedOptionName(locationOptions, filterValues.location)}
                options={locationOptions}
                selectedOptionId={filterValues.location}
                expanded={expandedDropdowns.location}
                onToggle={() => onDropdownToggle('location')}
                onClose={() => onDropdownClose('location')}
                onSelect={filterActions.setLocation}
                className={styles.filterDropdown}
                classNameList={styles.listLocation}
                dropdownAlign="right"
            />
        </div>
    );
};
